import React, { memo, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Progress } from '../../components/ui/progress';
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle 
} from '../../components/ui/dialog';
import { EngagementCard, AutomationTags } from '../../components/EngagementScoreBadge';
import { format, formatDistanceToNow } from 'date-fns';
import { toast } from 'sonner';
import {
  Mail, MessageSquare, Globe, Megaphone, Phone, Package,
  Bell, MousePointer, CheckCircle, XCircle, Eye, ExternalLink,
  TrendingUp, DollarSign, Calendar, Filter, BarChart3, Zap
} from 'lucide-react';

// Channel configurations
const CHANNEL_CONFIG = {
  email: { icon: Mail, color: 'bg-blue-500', lightColor: 'bg-blue-100 text-blue-700', label: 'Email' },
  sms: { icon: MessageSquare, color: 'bg-green-500', lightColor: 'bg-green-100 text-green-700', label: 'SMS' },
  social: { icon: Globe, color: 'bg-purple-500', lightColor: 'bg-purple-100 text-purple-700', label: 'Social' },
  ads: { icon: Megaphone, color: 'bg-amber-500', lightColor: 'bg-amber-100 text-amber-700', label: 'Ads' },
  phone: { icon: Phone, color: 'bg-slate-500', lightColor: 'bg-slate-100 text-slate-700', label: 'Phone' },
  direct_mail: { icon: Package, color: 'bg-orange-500', lightColor: 'bg-orange-100 text-orange-700', label: 'Direct Mail' },
  push_notification: { icon: Bell, color: 'bg-pink-500', lightColor: 'bg-pink-100 text-pink-700', label: 'Push' }
};

// Status configurations
const STATUS_CONFIG = {
  sent: { icon: Mail, color: 'text-slate-500', label: 'Sent' },
  delivered: { icon: CheckCircle, color: 'text-blue-500', label: 'Delivered' },
  opened: { icon: Eye, color: 'text-green-500', label: 'Opened' },
  clicked: { icon: MousePointer, color: 'text-purple-500', label: 'Clicked' },
  responded: { icon: MessageSquare, color: 'text-amber-500', label: 'Responded' },
  converted: { icon: TrendingUp, color: 'text-emerald-500', label: 'Converted' },
  unsubscribed: { icon: XCircle, color: 'text-red-500', label: 'Unsubscribed' },
  bounced: { icon: XCircle, color: 'text-red-500', label: 'Bounced' }
};

// Campaign Card Component
const CampaignCard = memo(({ campaign, onClick }) => {
  const channelConfig = CHANNEL_CONFIG[campaign.channel] || CHANNEL_CONFIG.email;
  const statusConfig = STATUS_CONFIG[campaign.status] || STATUS_CONFIG.sent;
  const ChannelIcon = channelConfig.icon;
  const StatusIcon = statusConfig.icon;

  return (
    <div 
      className="p-4 border rounded-lg hover:shadow-sm hover:border-blue-200 transition-all cursor-pointer"
      onClick={() => onClick(campaign)}
      data-testid={`campaign-card-${campaign.id}`}
    >
      <div className="flex items-start gap-3">
        <div className={`p-2 rounded-lg ${channelConfig.color} text-white`}>
          <ChannelIcon className="h-4 w-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <p className="font-medium truncate">{campaign.campaign_name}</p>
            <Badge variant="outline" className={channelConfig.lightColor}>
              {channelConfig.label}
            </Badge>
          </div>
          <p className="text-xs text-muted-foreground mb-2">
            {campaign.campaign_type} • {format(new Date(campaign.sent_at), 'MMM d, yyyy')}
          </p>
          
          {/* Status Timeline */}
          <div className="flex items-center gap-1">
            <Badge 
              variant="outline" 
              className={`text-xs ${campaign.status === 'bounced' || campaign.status === 'unsubscribed' ? 'border-red-300 text-red-600' : 'border-slate-200'}`}
            >
              <StatusIcon className={`h-3 w-3 mr-1 ${statusConfig.color}`} />
              {statusConfig.label}
            </Badge>
            
            {campaign.opened_at && campaign.status !== 'opened' && (
              <Badge variant="outline" className="text-xs border-green-200 text-green-600">
                <Eye className="h-3 w-3 mr-1" /> Opened
              </Badge>
            )}
            
            {campaign.clicked_at && (
              <Badge variant="outline" className="text-xs border-purple-200 text-purple-600">
                <MousePointer className="h-3 w-3 mr-1" /> Clicked
              </Badge>
            )}
            
            {campaign.converted_at && (
              <Badge variant="outline" className="text-xs border-emerald-200 text-emerald-600">
                <TrendingUp className="h-3 w-3 mr-1" /> 
                {campaign.conversion_value ? `$${campaign.conversion_value}` : 'Converted'}
              </Badge>
            )}
          </div>
        </div>
      </div>
      
      {campaign.content_preview && (
        <p className="text-xs text-muted-foreground mt-2 line-clamp-2 pl-11">
          {campaign.content_preview}
        </p>
      )}
    </div>
  );
});

CampaignCard.displayName = 'CampaignCard';

// Campaign Detail Dialog
const CampaignDetailDialog = ({ campaign, open, onClose }) => {
  if (!campaign) return null;
  
  const channelConfig = CHANNEL_CONFIG[campaign.channel] || CHANNEL_CONFIG.email;
  const ChannelIcon = channelConfig.icon;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <div className={`p-2 rounded-lg ${channelConfig.color} text-white`}>
              <ChannelIcon className="h-4 w-4" />
            </div>
            {campaign.campaign_name}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          {/* Campaign Info */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-xs text-muted-foreground">Channel</p>
              <p className="font-medium">{channelConfig.label}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Type</p>
              <p className="font-medium">{campaign.campaign_type}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Sent</p>
              <p className="font-medium">{format(new Date(campaign.sent_at), 'MMM d, yyyy h:mm a')}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Status</p>
              <Badge variant="outline">{campaign.status}</Badge>
            </div>
          </div>

          {/* Response Timeline */}
          <div>
            <p className="text-sm font-medium mb-2">Response Timeline</p>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <div className={`w-3 h-3 rounded-full ${campaign.sent_at ? 'bg-blue-500' : 'bg-slate-200'}`} />
                <span className="text-sm">Sent</span>
                {campaign.sent_at && (
                  <span className="text-xs text-muted-foreground ml-auto">
                    {format(new Date(campaign.sent_at), 'MMM d, h:mm a')}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <div className={`w-3 h-3 rounded-full ${campaign.delivered_at ? 'bg-blue-500' : 'bg-slate-200'}`} />
                <span className="text-sm">Delivered</span>
                {campaign.delivered_at && (
                  <span className="text-xs text-muted-foreground ml-auto">
                    {format(new Date(campaign.delivered_at), 'MMM d, h:mm a')}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <div className={`w-3 h-3 rounded-full ${campaign.opened_at ? 'bg-green-500' : 'bg-slate-200'}`} />
                <span className="text-sm">Opened</span>
                {campaign.opened_at && (
                  <span className="text-xs text-muted-foreground ml-auto">
                    {format(new Date(campaign.opened_at), 'MMM d, h:mm a')}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <div className={`w-3 h-3 rounded-full ${campaign.clicked_at ? 'bg-purple-500' : 'bg-slate-200'}`} />
                <span className="text-sm">Clicked</span>
                {campaign.clicked_at && (
                  <span className="text-xs text-muted-foreground ml-auto">
                    {format(new Date(campaign.clicked_at), 'MMM d, h:mm a')}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <div className={`w-3 h-3 rounded-full ${campaign.converted_at ? 'bg-emerald-500' : 'bg-slate-200'}`} />
                <span className="text-sm">Converted</span>
                {campaign.converted_at && (
                  <span className="text-xs text-muted-foreground ml-auto">
                    {format(new Date(campaign.converted_at), 'MMM d, h:mm a')}
                    {campaign.conversion_value && ` - $${campaign.conversion_value}`}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Content Preview */}
          {campaign.content_preview && (
            <div>
              <p className="text-sm font-medium mb-2">Content Preview</p>
              <div className="p-3 bg-slate-50 rounded-lg text-sm">
                {campaign.content_preview}
              </div>
            </div>
          )}

          {/* Link Clicked */}
          {campaign.link_clicked && (
            <div>
              <p className="text-sm font-medium mb-2">Link Clicked</p>
              <a 
                href={campaign.link_clicked} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-sm text-blue-600 hover:underline flex items-center gap-1"
              >
                {campaign.link_clicked}
                <ExternalLink className="h-3 w-3" />
              </a>
            </div>
          )}

          {/* Notes */}
          {campaign.notes && (
            <div>
              <p className="text-sm font-medium mb-2">Notes</p>
              <p className="text-sm text-muted-foreground">{campaign.notes}</p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Main Campaigns Tab Component
const CampaignsTab = memo(({ campaigns = [], stats = {}, onRefresh, contactId }) => {
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [channelFilter, setChannelFilter] = useState('all');

  // Filter campaigns by channel
  const filteredCampaigns = useMemo(() => {
    if (channelFilter === 'all') return campaigns;
    return campaigns.filter(c => c.channel === channelFilter);
  }, [campaigns, channelFilter]);

  // Calculate engagement rate
  const engagementRate = useMemo(() => {
    if (stats.total_campaigns === 0) return 0;
    return Math.round((stats.opened / stats.total_campaigns) * 100);
  }, [stats]);

  const clickRate = useMemo(() => {
    if (stats.opened === 0) return 0;
    return Math.round((stats.clicked / stats.opened) * 100);
  }, [stats]);

  const conversionRate = useMemo(() => {
    if (stats.clicked === 0) return 0;
    return Math.round((stats.converted / stats.clicked) * 100);
  }, [stats]);

  const handleCampaignClick = (campaign) => {
    setSelectedCampaign(campaign);
    setDetailOpen(true);
  };

  return (
    <ScrollArea className="h-[600px]">
      <div className="p-4 space-y-4">
        {/* Engagement Score Card */}
        {contactId && (
          <EngagementCard 
            contactId={contactId} 
            onEvaluateRules={(result) => {
              if (result.rules_triggered > 0) {
                toast.success(`${result.rules_triggered} automation rule(s) triggered`);
                if (onRefresh) onRefresh();
              } else {
                toast.info('No automation rules triggered');
              }
            }}
          />
        )}

        {/* Stats Overview */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card className="bg-gradient-to-br from-blue-50 to-white">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-1">
                <Megaphone className="h-4 w-4 text-blue-600" />
                <span className="text-xs text-muted-foreground">Total Campaigns</span>
              </div>
              <p className="text-2xl font-bold text-blue-600">{stats.total_campaigns || 0}</p>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-green-50 to-white">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-1">
                <Eye className="h-4 w-4 text-green-600" />
                <span className="text-xs text-muted-foreground">Open Rate</span>
              </div>
              <p className="text-2xl font-bold text-green-600">{engagementRate}%</p>
              <Progress value={engagementRate} className="h-1 mt-1" />
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-purple-50 to-white">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-1">
                <MousePointer className="h-4 w-4 text-purple-600" />
                <span className="text-xs text-muted-foreground">Click Rate</span>
              </div>
              <p className="text-2xl font-bold text-purple-600">{clickRate}%</p>
              <Progress value={clickRate} className="h-1 mt-1" />
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-emerald-50 to-white">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-1">
                <DollarSign className="h-4 w-4 text-emerald-600" />
                <span className="text-xs text-muted-foreground">Conversion Value</span>
              </div>
              <p className="text-2xl font-bold text-emerald-600">
                ${stats.total_conversion_value?.toLocaleString() || 0}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Channel Filter */}
        <div className="flex items-center gap-2 flex-wrap">
          <Filter className="h-4 w-4 text-muted-foreground" />
          <Button
            variant={channelFilter === 'all' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setChannelFilter('all')}
          >
            All ({stats.total_campaigns || 0})
          </Button>
          {Object.entries(CHANNEL_CONFIG).map(([key, config]) => {
            const count = campaigns.filter(c => c.channel === key).length;
            if (count === 0) return null;
            return (
              <Button
                key={key}
                variant={channelFilter === key ? 'default' : 'outline'}
                size="sm"
                className={channelFilter === key ? config.color : ''}
                onClick={() => setChannelFilter(key)}
              >
                <config.icon className="h-3 w-3 mr-1" />
                {config.label} ({count})
              </Button>
            );
          })}
        </div>

        {/* Channel Breakdown */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <BarChart3 className="h-4 w-4" />
              Channel Breakdown
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-4 gap-2 text-center">
              <div className="p-2 rounded bg-blue-50">
                <Mail className="h-4 w-4 mx-auto text-blue-600 mb-1" />
                <p className="text-lg font-bold text-blue-600">{stats.total_emails || 0}</p>
                <p className="text-xs text-muted-foreground">Emails</p>
              </div>
              <div className="p-2 rounded bg-green-50">
                <MessageSquare className="h-4 w-4 mx-auto text-green-600 mb-1" />
                <p className="text-lg font-bold text-green-600">{stats.total_sms || 0}</p>
                <p className="text-xs text-muted-foreground">SMS</p>
              </div>
              <div className="p-2 rounded bg-purple-50">
                <Globe className="h-4 w-4 mx-auto text-purple-600 mb-1" />
                <p className="text-lg font-bold text-purple-600">{stats.total_social || 0}</p>
                <p className="text-xs text-muted-foreground">Social</p>
              </div>
              <div className="p-2 rounded bg-amber-50">
                <Megaphone className="h-4 w-4 mx-auto text-amber-600 mb-1" />
                <p className="text-lg font-bold text-amber-600">{stats.total_ads || 0}</p>
                <p className="text-xs text-muted-foreground">Ads</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Campaign List */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-sm flex items-center gap-2">
              <Calendar className="h-4 w-4 text-blue-600" />
              Campaign History ({filteredCampaigns.length})
            </h3>
          </div>
          
          {filteredCampaigns.length > 0 ? (
            <div className="space-y-3">
              {filteredCampaigns.map((campaign) => (
                <CampaignCard 
                  key={campaign.id} 
                  campaign={campaign} 
                  onClick={handleCampaignClick}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <Megaphone className="h-12 w-12 text-slate-300 mx-auto mb-3" />
              <p className="text-muted-foreground">No campaign history</p>
              <p className="text-sm text-muted-foreground mt-1">
                Campaigns targeting this contact will appear here
              </p>
            </div>
          )}
        </div>

        {/* Campaign Detail Dialog */}
        <CampaignDetailDialog
          campaign={selectedCampaign}
          open={detailOpen}
          onClose={() => setDetailOpen(false)}
        />
      </div>
    </ScrollArea>
  );
});

CampaignsTab.displayName = 'CampaignsTab';

export default CampaignsTab;
